import type { TFunction } from "i18next";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface ConformityRef {
  id?: number;
}

interface PersonRef {
  firstName?: string;
  lastName?: string;
}

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface InspectionData {
  controlDate?: string;
  conformity?: ConformityRef;
  resource?: { name?: string; tags?: TagRef[] };
  flow?: { name?: string };
  assignedToUser?: PersonRef | null;
  submittedByUser?: PersonRef | null;
  employee?: string;
}

interface InfoInspectionCardProps {
  t: TFunction;
  data: InspectionData | null | undefined;
}

function getConformityLabel(conformityId: number | undefined, t: TFunction) {
  if (conformityId === 1) return t("inspections.info-card.complaint");
  if (conformityId === 2) return t("inspections.items.scheduled");
  return t("inspections.info-card.non-complaint");
}

function getConformityClass(conformityId: number | undefined) {
  if (conformityId === 1) return "text-emerald-600 dark:text-emerald-400";
  if (conformityId === 2) return "text-sky-600 dark:text-sky-400";
  return "text-destructive";
}

function getUserName(data: InspectionData | null | undefined) {
  const isScheduled = data?.conformity?.id === 2;
  if (isScheduled && data?.assignedToUser) {
    return (
      `${data.assignedToUser.firstName || ""} ${data.assignedToUser.lastName || ""}`.trim() ||
      "-"
    );
  }
  if (data?.submittedByUser) {
    return (
      `${data.submittedByUser.firstName || ""} ${data.submittedByUser.lastName || ""}`.trim() ||
      "-"
    );
  }
  return data?.employee || "-";
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

export default function InfoInspectionCard({
  t,
  data,
}: InfoInspectionCardProps) {
  const conformityId = data?.conformity?.id;
  return (
    <Card>
      <CardContent className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <dl className="space-y-3">
          <Row label={`${t("inspections.items.date")}:`}>
            {data?.controlDate}
          </Row>
          <Row label={`${t("inspections.info-card.employee")}:`}>
            {getUserName(data)}
          </Row>
          <Row label={`${t("inspections.info-card.state")}:`}>
            <span className={cn(getConformityClass(conformityId))}>
              {getConformityLabel(conformityId, t)}
            </span>
          </Row>
        </dl>
        <dl className="space-y-3">
          <Row label={`${t("inspections.items.resource")}:`}>
            {data?.resource?.name}
          </Row>
          <Row label={`${t("inspections.items.tags")}:`}>
            <div className="flex flex-wrap gap-1">
              {(data?.resource?.tags ?? []).map((tag) => (
                <Badge
                  key={tag.id}
                  style={{
                    backgroundColor: tag.color ?? undefined,
                    color: tag.color ? "#fff" : undefined,
                  }}
                >
                  {tag.name}
                </Badge>
              ))}
            </div>
          </Row>
          <Row label={`${t("inspections.items.flow-type")}:`}>
            {data?.flow?.name}
          </Row>
        </dl>
      </CardContent>
    </Card>
  );
}
